// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import { Gzip } from 'fflate';
import type { AppliedFinding } from './core/apply';
import type {
  FromReader,
  FromScanner,
  Pass,
  PseudonymEntry,
  ScanResultMsg,
  ScanSettings,
} from './core/protocol';
import type { Sink } from './io/sink';

export interface PassProgress {
  readonly compressedRead: number;
  readonly compressedTotal: number;
  readonly decompressedRead: number;
  readonly records: number;
  readonly findings: number;
  readonly bytesPerSecond: number;
  readonly workers: number;
}

export interface PassResult {
  readonly records: number;
  readonly lines: number;
  readonly malformed: number;
  readonly decompressedBytes: number;
  readonly written: number;
  readonly cancelled: boolean;
}

export interface PassHooks {
  onProgress(p: PassProgress): void;
  onFindings(f: AppliedFinding[]): void;
  onEntries(e: PseudonymEntry[]): void;
  onEvent(level: 'info' | 'good' | 'warn' | 'error', message: string): void;
}

/**
 * Touch devices get three workers and 1 MiB chunks; desktop gets up to eight and 4 MiB.
 *
 * `hardwareConcurrency` is clamped by Safari to 4 or 8 specifically to defeat fingerprinting,
 * so it is a ceiling rather than a core count. The UI prints the pool size only after the pool
 * exists, never a prediction.
 */
export function poolSize(): number {
  const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  if (coarse) return 3;
  return Math.max(1, Math.min((navigator.hardwareConcurrency || 4) - 1, 8));
}

export function chunkBytes(): number {
  const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  return coarse ? 1024 * 1024 : 4 * 1024 * 1024;
}

export interface RunOptions {
  readonly file: File;
  readonly pass: Pass;
  readonly settings: ScanSettings;
  readonly sink: Sink | null;
  readonly gzipOutput: boolean;
  readonly signal: AbortSignal;
}

export function runPass(opts: RunOptions, hooks: PassHooks): Promise<PassResult> {
  return new Promise<PassResult>((resolve, reject) => {
    const workers = poolSize();
    const chunk = chunkBytes();

    const reader = new Worker(new URL('./workers/reader.worker.ts', import.meta.url), {
      type: 'module',
    });
    const scanners: Worker[] = [];
    const channels: MessageChannel[] = [];
    for (let i = 0; i < workers; i++) {
      scanners.push(new Worker(new URL('./workers/scanner.worker.ts', import.meta.url), { type: 'module' }));
      channels.push(new MessageChannel());
    }

    let records = 0;
    let lines = 0;
    let malformed = 0;
    let findingCount = 0;
    let decompressedBytes = 0;
    let compressedRead = 0;
    let totalChunks = -1;
    let received = 0;
    let drained = 0;
    let settled = false;
    let cancelled = false;

    // Cleaned chunks arrive out of order across the pool, and the output file must not be.
    // In-flight chunks are bounded by the credit protocol, so this map is bounded too.
    const pendingWrites = new Map<number, Uint8Array>();
    let nextWrite = 0;
    let writeChain: Promise<void> = Promise.resolve();
    let writeError: Error | null = null;

    const started = Date.now();
    const rates: number[] = [];
    let lastSample = started;
    let lastBytes = 0;

    let gz: Gzip | null = null;
    if (opts.pass === 2 && opts.gzipOutput && opts.sink) {
      gz = new Gzip({ level: 6, mtime: 0 });
      gz.ondata = (data) => {
        const copy = data.slice();
        writeChain = writeChain.then(() => opts.sink!.write(copy)).catch(captureWriteError);
      };
    }

    function captureWriteError(e: unknown): void {
      if (!writeError) writeError = e instanceof Error ? e : new Error(String(e));
    }

    function cleanup(): void {
      reader.terminate();
      for (const s of scanners) s.terminate();
    }

    function fail(e: Error): void {
      if (settled) return;
      settled = true;
      cleanup();
      reject(e);
    }

    /**
     * Drain every queued write, flush the gzip tail, and settle.
     *
     * Deliberately a standalone async function rather than another link in `writeChain`. The
     * obvious version — `writeChain = writeChain.then(async () => { ...; await writeChain; })` —
     * awaits the very promise it is constructing and deadlocks: the run reaches 100% of the file
     * and then hangs forever. Found in the browser dry-run, not by reading the code.
     */
    async function finalize(): Promise<void> {
      await writeChain;
      if (gz) {
        // Pushing the final block makes fflate emit its trailer through ondata, which appends
        // to writeChain — so re-read writeChain AFTER the push, never before.
        gz.push(new Uint8Array(0), true);
        await writeChain;
      }
      if (writeError) throw writeError;
    }

    function maybeFinish(): void {
      if (settled) return;
      if (totalChunks < 0 || received < totalChunks) return;
      if (drained < scanners.length) return;
      settled = true;
      finalize()
        .then(() => {
          cleanup();
          resolve({
            records,
            lines,
            malformed,
            decompressedBytes,
            written: opts.sink?.written ?? 0,
            cancelled,
          });
        })
        .catch((e: unknown) => {
          cleanup();
          reject(e instanceof Error ? e : new Error(String(e)));
        });
    }

    function flushWrites(): void {
      if (!opts.sink) return;
      for (;;) {
        const next = pendingWrites.get(nextWrite);
        if (!next) break;
        pendingWrites.delete(nextWrite);
        nextWrite++;
        if (gz) gz.push(next, false);
        else writeChain = writeChain.then(() => opts.sink!.write(next)).catch(captureWriteError);
      }
    }

    function sampleRate(): number {
      const now = Date.now();
      if (now - lastSample >= 500) {
        const dt = (now - lastSample) / 1000;
        const db = compressedRead - lastBytes;
        if (dt > 0) {
          rates.push(db / dt);
          if (rates.length > 60) rates.shift();
        }
        lastSample = now;
        lastBytes = compressedRead;
      }
      if (rates.length === 0) return 0;
      const sorted = [...rates].sort((a, b) => a - b);
      return sorted[Math.floor(sorted.length / 2)]!;
    }

    reader.onmessage = (ev: MessageEvent) => {
      const m = ev.data as FromReader;
      if (m.kind === 'progress') {
        compressedRead = m.compressedRead;
        decompressedBytes = m.decompressedRead;
        hooks.onProgress({
          compressedRead: m.compressedRead,
          compressedTotal: m.compressedTotal,
          decompressedRead: m.decompressedRead,
          records,
          findings: findingCount,
          bytesPerSecond: sampleRate(),
          workers,
        });
      } else if (m.kind === 'done') {
        totalChunks = m.totalChunks;
        decompressedBytes = m.decompressedBytes;
        maybeFinish();
      } else if (m.kind === 'error') {
        if (m.message === 'cancelled') {
          cancelled = true;
          totalChunks = received;
          drained = scanners.length;
          maybeFinish();
          return;
        }
        fail(new Error(m.message));
      }
    };
    reader.onerror = (e) => fail(new Error(`Reader worker failed: ${e.message}`));

    for (let i = 0; i < scanners.length; i++) {
      const s = scanners[i]!;
      s.onmessage = (ev: MessageEvent) => {
        const m = ev.data as FromScanner;
        if (m.kind === 'drained') {
          drained++;
          maybeFinish();
          return;
        }
        if (m.kind === 'error') {
          fail(new Error(m.message));
          return;
        }
        const r = m as ScanResultMsg;
        if (r.entries.length > 0) hooks.onEntries(r.entries);
        if (r.seq < 0) return;
        received++;
        records += r.records;
        lines += r.lines;
        malformed += r.malformed;
        if (r.findings.length > 0) {
          findingCount += r.findings.length;
          hooks.onFindings(r.findings);
        }
        if (r.cleanOut) {
          pendingWrites.set(r.seq, new Uint8Array(r.cleanOut, 0, r.cleanLength));
          flushWrites();
        }
        maybeFinish();
      };
      s.onerror = (e) => fail(new Error(`Scanner worker failed: ${e.message}`));
      s.postMessage({ kind: 'init', port: channels[i]!.port2, pass: opts.pass, settings: opts.settings }, [
        channels[i]!.port2,
      ]);
    }

    opts.signal.addEventListener(
      'abort',
      () => {
        cancelled = true;
        reader.postMessage({ kind: 'cancel' });
      },
      { once: true },
    );

    hooks.onEvent('info', `Pass ${opts.pass} starting across ${workers} worker${workers === 1 ? '' : 's'}.`);

    const ports = channels.map((c) => c.port1);
    reader.postMessage(
      { kind: 'start', file: opts.file, ports, chunkBytes: chunk, pass: opts.pass },
      ports,
    );
  });
}
